import React, {useRef, useEffect} from 'react';
import {useDialog} from '@react-aria/dialog';
import {useOverlay, usePreventScroll, useModal, OverlayProvider, OverlayContainer} from '@react-aria/overlays';
import {FocusScope} from '@react-aria/focus';
import {AriaDialogProps} from '@react-types/dialog';
import clsx from 'clsx';
import {CloseIcon} from "./Icons";
import {IconButton} from './IconButton';

interface ModalProps extends AriaDialogProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    isDismissable?: boolean;
    className?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'full';
    hideCloseButton?: boolean;
}

// Internal props without isOpen (it's always true when this component renders)
interface ModalDialogProps extends Omit<ModalProps, 'isOpen'> {
}

// Internal component that uses the overlay hooks
const ModalDialog: React.FC<ModalDialogProps> = ({
                                                     onClose,
                                                     children,
                                                     title,
                                                     isDismissable = true,
                                                     className,
                                                     size = 'md',
                                                     hideCloseButton = false,
                                                     ...dialogProps
                                                 }) => {

    const overlayRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Handle overlay interactions
    const {overlayProps, underlayProps} = useOverlay(
        {
            isOpen: true,
            onClose,
            isDismissable,
            isKeyboardDismissDisabled: !isDismissable,
        },
        overlayRef
    );

    // Prevent scrolling while modal is open
    usePreventScroll();

    // Make the overlay a modal (traps focus)
    const {modalProps} = useModal();

    // Get props for the dialog
    const {dialogProps: ariaDialogProps, titleProps} = useDialog(
        dialogProps,
        dialogRef
    );

    // Set aria-hidden on root when modal is open
    useEffect(() => {
        const root = document.getElementById('root');
        if (root) {
            root.setAttribute('aria-hidden', 'true');
            return () => {
                root.removeAttribute('aria-hidden');
            };
        }
    }, []);

    // Size classes
    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-2xl',
        xxl: 'max-w-5xl',
        xxxl: 'max-w-6xl',
        full: 'max-w-full mx-4',
    };

    return (
        <div role="presentation" className="fixed inset-0 z-[1300] flex items-center justify-center">
            {/* Backdrop - separate layer */}
            <div
                {...underlayProps}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in z-[1300]"
                onClick={isDismissable ? onClose : undefined}
                style={{
                    opacity: 1,
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            />

            {/* Focus Sentinel Start */}
            <div tabIndex={0} data-testid="sentinelStart" style={{width: 0, height: 0}}/>

            {/* Modal Dialog */}
            <div className="fixed inset-0 z-[1300] flex items-center justify-center">
                <FocusScope contain restoreFocus autoFocus>
                    <div
                        {...overlayProps}
                        {...modalProps}
                        {...ariaDialogProps}
                        ref={overlayRef}
                        tabIndex={-1}
                        className={clsx(
                            'relative w-full animate-modal-in max-h-[90vh] overflow-y-auto',
                            sizeClasses[size],
                            className
                        )}
                    >
                        <div
                            ref={dialogRef}
                            className="card-container-no-border flex-col overflow-hidden"
                        >
                            {/* Header */}
                            {title && (
                                <div
                                    className="flex items-center justify-between px-6 pt-4 pb-2"
                                    style={{borderColor: 'var(--color-field-border)'}}
                                >
                                    <h2
                                        {...titleProps}
                                        className="text-lg font-semibold"
                                        style={{color: 'var(--color-field-text)'}}
                                    >
                                        {title}
                                    </h2>
                                    {isDismissable && !hideCloseButton && (
                                        <IconButton
                                            aria-label="Close"
                                            variant="ghost"
                                            onPress={onClose}
                                        >
                                            <CloseIcon/>
                                        </IconButton>
                                    )}
                                </div>
                            )}

                            {/* Content */}
                            <div className="px-6 pb-4 pt-2">
                                {children}
                            </div>
                        </div>
                    </div>
                </FocusScope>
            </div>

            {/* Focus Sentinel End */}
            <div tabIndex={0} data-testid="sentinelEnd" style={{width: 0, height: 0}}/>
        </div>
    );
};

// Main Modal component that wraps with provider
export const Modal: React.FC<ModalProps> = ({isOpen, ...props}) => {

    if (!isOpen) {
        return null;
    }

    return (
        <OverlayProvider>
            <OverlayContainer>
                <ModalDialog {...props} />
            </OverlayContainer>
        </OverlayProvider>
    );
};

// Optional: Helper components for common modal patterns
export const ModalActions: React.FC<{ children: React.ReactNode; className?: string }> = ({
                                                                                              children,
                                                                                              className,
                                                                                          }) => {
    return (
        <div className={clsx(
            'flex items-center justify-between gap-3 py-4',
            className
        )}
             style={{borderColor: 'var(--color-field-border)'}}
        >
            {children}
        </div>
    );
};

export const ModalBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
                                                                                           children,
                                                                                           className,
                                                                                       }) => {
    return (
        <div className={clsx('py-4', className)} style={{color: 'var(--color-field-text)'}}>
            {children}
        </div>
    );
};